import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { FormattedMessage } from '../../util/reactIntl';
import NamedLink from '../NamedLink/NamedLink';
import SellerActionReminder from '../../containers/SellerDashboardPage/SellerActionReminder';
import { fetchFreightQueueCount } from '../../ducks/freightQueue.duck';
import {
  visibleGroupsFor,
  groupIdForPage,
  groupBadgeCount,
} from './sellerNavGroups';

import css from './SellerNav.module.css';

/**
 * Seller navigation, grouped into four areas (P25009-386).
 *
 * Replaces the flat row of ten tabs in UserNav, where half the items needed
 * sideways scrolling with no hint they existed, and the money page was only
 * reachable through the notification bell.
 *
 * Two orientations from one component:
 *
 *   layout="bar"     (default) a grouped band under the topbar. Items WRAP; they
 *                    never scroll sideways and nothing hides behind a menu, which
 *                    is what AC-2 actually asks for. Used by the existing seller
 *                    pages, which all render their nav in the topbar slot.
 *   layout="sidebar" a vertical menu, for pages laid out with a side column.
 *
 * BR-5 asks for a side menu on desktop. Doing that everywhere means converting
 * every seller page from LayoutSingleColumn to a two-column layout — a change
 * with a much wider blast radius than this story, so the sidebar orientation
 * exists and is used where a side column already makes sense, while the bar
 * satisfies the "nothing hidden" requirement everywhere today.
 *
 * On mobile the areas become collapsible sections (BR-5), with the area holding
 * the current page open on arrival so a seller never lands on a closed menu.
 */

const Chevron = ({ open }) => (
  <svg
    className={classNames(css.chevron, { [css.chevronOpen]: open })}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const Badge = ({ count }) => {
  if (!count) return null;
  return (
    <span className={css.badge}>
      {/* Capped so a seller with 300 unread messages does not blow out the
        * layout. The exact number lives on the page the badge points at. */}
      {count > 99 ? '99+' : count}
      <span className={css.srOnly}>
        <FormattedMessage id="SellerNav.needsAction" />
      </span>
    </span>
  );
};

const NavItem = ({ item, currentPage, badges }) => {
  const rawBadge = item.badge ? badges[item.badge] : null;
  const count = rawBadge === true ? 1 : typeof rawBadge === 'number' ? rawBadge : 0;
  const isBoolBadge = rawBadge === true;

  return (
    <li className={css.item}>
      <NamedLink
        name={item.route}
        params={item.routeParams}
        className={classNames(css.link, { [css.linkActive]: currentPage === item.route })}
      >
        <span className={css.linkLabel}>
          <FormattedMessage id={item.labelKey} />
        </span>
        {/* A boolean flag shows a dot, not a number: "1" beside Utbetalningar
          * would read as one pending payout rather than "look here". */}
        {isBoolBadge ? (
          <span className={css.dot} aria-hidden="true" />
        ) : (
          <Badge count={count} />
        )}
        {isBoolBadge ? (
          <span className={css.srOnly}>
            <FormattedMessage id="SellerNav.needsAction" />
          </span>
        ) : null}
      </NamedLink>
    </li>
  );
};

const SellerNav = props => {
  const {
    className,
    rootClassName,
    currentPage,
    currentUser,
    badges = {},
    layout = 'bar',
  } = props;

  /* The Frakt queue count (P25009-450 BR-6) is the one badge the nav fetches
   * for itself: it must light up on EVERY seller page, and no host page knows
   * it. The duck's staleness window keeps this from being a query per
   * navigation. Other badge counts still come from the host page's props. */
  const dispatch = useDispatch();
  const freightQueueCount = useSelector(state => state.freightQueue?.count || 0);
  const userId = currentUser?.id?.uuid;
  useEffect(() => {
    if (userId) {
      dispatch(fetchFreightQueueCount());
    }
  }, [userId, dispatch]);
  const allBadges = { ...badges, freightQueue: freightQueueCount };

  const groups = visibleGroupsFor(currentUser);
  const activeGroupId = groupIdForPage(currentPage);

  /* Mobile accordion state. Everything starts CLOSED: with the rail carrying
   * every account item (P25009-431), an open area pushed the page content
   * below the fold on a phone. Five compact heading rows, and the seller
   * opens the area they want. Desktop ignores this — all items are visible.
   * The proper phone-native treatment is ticket P25009-432. */
  const [openGroupId, setOpenGroupId] = useState(null);

  if (groups.length === 0) return null;

  const isSidebar = layout === 'sidebar';

  return (
    <>
      {/* BR-4: the one-line reminder rides along with the nav, so every seller
        * page gets it without each page having to remember. Renders nothing
        * unless something urgent is open. */}
      <SellerActionReminder currentPage={currentPage} />
      <nav
        className={classNames(rootClassName || css.root, className, {
          [css.sidebar]: isSidebar,
        })}
        aria-label="Säljmeny"
      >
        <div className={classNames(css.inner, { [css.innerSidebar]: isSidebar })}>
        {/* The overview sits above the areas rather than inside one: it is the
          * home the four areas hang beneath, not a fifth tool. */}
        <NamedLink
          name="SellerDashboardPage"
          className={classNames(css.overviewLink, {
            [css.linkActive]: currentPage === 'SellerDashboardPage',
          })}
        >
          <FormattedMessage id="SellerNav.overview" />
        </NamedLink>

        {groups.map(group => {
          const groupCount = groupBadgeCount(group, allBadges);
          const isOpen = openGroupId === group.id;

          return (
            <div
              key={group.id}
              className={classNames(css.group, {
                [css.groupOpen]: isOpen,
                [css.groupActive]: activeGroupId === group.id,
              })}
            >
              {/* A button on mobile (it toggles), a plain heading on desktop
                * (everything is already visible). One element, so the markup
                * does not diverge between breakpoints. */}
              <button
                type="button"
                className={css.groupHeading}
                onClick={() => setOpenGroupId(isOpen ? null : group.id)}
                aria-expanded={isOpen}
                aria-controls={`seller-nav-${group.id}`}
              >
                <span className={css.groupLabel}>
                  <FormattedMessage id={group.labelKey} />
                </span>
                <Badge count={groupCount} />
                <Chevron open={isOpen} />
              </button>

              <ul id={`seller-nav-${group.id}`} className={css.items}>
                {group.items.map(item => (
                  <NavItem
                    key={item.id}
                    item={item}
                    currentPage={currentPage}
                    badges={allBadges}
                  />
                ))}
              </ul>
            </div>
            );
          })}
        </div>
      </nav>
    </>
  );
};

const { string, object, oneOf } = PropTypes;

SellerNav.propTypes = {
  className: string,
  rootClassName: string,
  currentPage: string,
  currentUser: object,
  /* { drafts: number, newOrders: number, payoutBlocked: boolean } — whichever
   * counts the host page has. Missing keys simply show no badge. */
  badges: object,
  layout: oneOf(['bar', 'sidebar']),
};

export default SellerNav;
